import React, { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import cx from "classnames";
import { disableBodyScroll, enableBodyScroll } from "body-scroll-lock";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faList, faPlane, faMapMarkerAlt, faTimes } from "@fortawesome/free-solid-svg-icons";
import Tippy from "@tippyjs/react/headless";
import { AppContext } from "../../context";
import { BRANDS, MODES } from "../../configs";
import { InputWrapper } from "../InputWrapper";
import { Typeahead } from "../Typeahead";
import { ListView } from "../ListView";
import { renderReactChildrenWithData } from "../../utils";
import {
	FLIGHTS_TO_FIELD_ID,
	PACKAGES_FROM_FIELD_ID,
	PACKAGES_TO_FIELD_ID,
} from "../../configs/const";

import styles from "./TypeaheadListViewInput.scss";

const defaultLabels = {
	noResults: "No results found",
	list: "List",
};

const TypeaheadListViewInput = ({
	id,
	mode,
	label,
	brand,
	className,
	placeholder,
	helperText,

	error,
	isLoading,
	isOnlyToField,
	isOnlyFromField,
	isHasSelectedItem,

	data,
	initialItem,
	selectedItem,

	appRoot,
	reactDOM,
	children,

	onInput,
	onChange,
	resetError,
	handleFocus,
	setIsHasSelectedItem,
	setShowPassengerInput,
	setNextButtonDisabled,
	setSovMobileIsFromInputEmpty,
	handleSovMobileInputSelectionToView,

	labels: providedLabels,
}) => {
	const [useAppState] = AppContext.useAppContext();
	const labels = { ...defaultLabels, ...providedLabels };
	const [isListViewOpen, setIsListViewOpen] = useState(false);
	const isSovBrand = useAppState.brand.toLowerCase() === BRANDS.SOV.toLowerCase();
	const isSovMobile = useAppState.sovSimplifiedSearchBar && isSovBrand;

	const referenceElement = useRef(null);

	useEffect(() => {
		if (referenceElement?.current) {
			if (isListViewOpen) {
				disableBodyScroll(referenceElement.current, {
					reserveScrollBarGap: true,
				});
			} else {
				enableBodyScroll(referenceElement.current);
			}
		}
	}, [isListViewOpen, referenceElement]);

	let sovFromAndToIconName = "";
	if (id === PACKAGES_FROM_FIELD_ID) {
		sovFromAndToIconName = faPlane;
	}
	if (id === PACKAGES_TO_FIELD_ID) {
		sovFromAndToIconName = faMapMarkerAlt;
	}

	return (
		<InputWrapper
			ref={referenceElement}
			className={cx(styles.inputWrapper, className, {
				[styles.listViewOpen]: brand.toLowerCase() === BRANDS.SWG.toLowerCase() && isListViewOpen,
				[styles[brand]]: [styles[brand]],
			})}
			id={id}
			brand={brand}
			label={label}
			error={error}
			helperText={helperText}
			hasSelectedItem={!!isHasSelectedItem}
			isListOpen={isListViewOpen}
			onBlur={() => !isListViewOpen}>
			{({ isFocused, handleOnFocus, handleOnBlur }) => (
				<Typeahead
					id={id}
					mode={mode}
					brand={brand}
					className={styles.typeahead}
					error={error}
					loading={isLoading}
					isFocused={isFocused}
					isMobileSOV={isSovMobile}
					isHasSelectedItem={isHasSelectedItem}
					data={data}
					placeholder={placeholder}
					initialSelectedItem={initialItem}
					selectedItem={selectedItem}
					environment={reactDOM}
					onBlur={handleOnBlur}
					handleSovMobileInputSelectionToView={handleSovMobileInputSelectionToView}
					setIsHasSelectedItem={setIsHasSelectedItem}
					onFocus={() => {
						resetError();
						handleOnFocus();
						
						if(isSovMobile){
							handleFocus();
							if(!selectedItem?.label){
								setNextButtonDisabled(true)
							}
							setShowPassengerInput(false)
						}
					}}
					onChange={value => {
						resetError();
						onChange(value);

						if(isSovMobile){
							if(!value) {
								setNextButtonDisabled(true)
								setIsHasSelectedItem(false)
							}
							
							if(value?.label) {
								setNextButtonDisabled(false)
								setIsHasSelectedItem(true)
							}
						}
					}}
					onInput={value => {
						if(isSovMobile){
							if (!value) {
								setSovMobileIsFromInputEmpty(true);
							} else {
								setSovMobileIsFromInputEmpty(false);
							}
						}

						resetError();
						onInput(value);
					}}
					icon={
						<span
							className={cx({
								[styles[brand]]: styles[brand],
								[styles.listViewButtonError]: error,
								[styles.listViewButton]: brand !== BRANDS.SOV,
								[styles.inputIcon]: isSovBrand,
							})}
							focus-trap-toggle="true">
							<FontAwesomeIcon
								icon={isSovBrand ? sovFromAndToIconName : faList}
								className={cx(styles.listViewIcon, {
									[styles.listViewIconGoingTo]:
										id === PACKAGES_TO_FIELD_ID || id === FLIGHTS_TO_FIELD_ID,
								})}
							/>{" "}
							{brand !== BRANDS.SOV && labels.list}
						</span>
					}
					iconClose={
						(inputValue, reset) =>
							isSovBrand &&
							useAppState.sovSimplifiedSearchBar &&
							(isOnlyFromField || isOnlyToField) &&
							inputValue && (
								<button
									type="button"
									onClick={() => {
										reset();
										setNextButtonDisabled(true)
									}}
									className={cx(styles.sovCloseIcon, {
										[styles[brand]]: styles[brand],
									})}
									focus-trap-toggle="true">
									<FontAwesomeIcon icon={faTimes} className={styles.listViewIcon} />
								</button>
							)
						// eslint-disable-next-line react/jsx-curly-newline
					}
					onClickIcon={state => {
						resetError();
						setIsListViewOpen(!isListViewOpen);

						if (state === "open") {
							return false;
						}

						return true;
					}}
					labels={labels}>
					{isListViewOpen && (
						<Tippy
							reference={referenceElement.current}
							visible={isListViewOpen}
							interactive={true}
							appendTo={referenceElement.current}
							placement="bottom"
							popperOptions={{
								strategy: "fixed",
								modifiers: [
									{
										name: "flip",
										enabled: false,
									},
									{
										name: "offset",
										enabled: true,
										options: {
											offset: [0, 8],
										},
									},
									{
										name: "preventOverflow",
										enabled: true,
										options: {
											padding: 0,
											boundary: appRoot,
										},
									},
									{
										name: "maxHeight",
										enabled: true,
										phase: "afterMain",
										requiresIfExists: ["offset"],
										fn({ state }) {
											const height =
												window.innerHeight - state.elements.popper.getBoundingClientRect().top - 16;

											// eslint-disable-next-line no-param-reassign
											state.styles.popper = {
												...state.styles.popper,
												maxHeight: `${height}px`,
											};
										},
									},
								],
							}}
							render={
								attrs =>
									useAppState.brand !== BRANDS.SOV && (
										<ListView
											// eslint-disable-next-line react/jsx-props-no-spreading
											{...attrs}
											brand={brand}
											reactDOM={reactDOM}
											appRoot={appRoot}
											open={isListViewOpen}
											className={styles.container}
											onClose={() => {
												setIsListViewOpen(false);
												handleOnBlur(true);
											}}>
											{renderReactChildrenWithData(children, {
												onSelectItem: value => {
													onChange(value);
													setIsListViewOpen(false);
													handleOnBlur();
													
													if(value?.label){
														setIsHasSelectedItem(true);
													}
												},
											})}
										</ListView>
									)
								// eslint-disable-next-line react/jsx-curly-newline
							}
						/>
					)}
				</Typeahead>
			)}
		</InputWrapper>
	);
};

TypeaheadListViewInput.propTypes = {
	id: PropTypes.string.isRequired,
	mode: PropTypes.string,
	brand: PropTypes.oneOf(Object.keys(BRANDS)),
	className: PropTypes.string,
	helperText: PropTypes.string,
	label: PropTypes.string.isRequired,
	placeholder: PropTypes.string,

	error: PropTypes.bool,
	isLoading: PropTypes.bool,
	isOnlyToField: PropTypes.bool,
	isOnlyFromField: PropTypes.bool,
	isHasSelectedItem: PropTypes.bool,

	initialItem: PropTypes.shape({
		label: PropTypes.string,
		value: PropTypes.string,
	}),
	selectedItem: PropTypes.shape({
		label: PropTypes.string,
		value: PropTypes.string,
	}),

	data: PropTypes.arrayOf(
		PropTypes.shape({
			title: PropTypes.string,
			items: PropTypes.arrayOf(
				PropTypes.shape({
					label: PropTypes.string,
					value: PropTypes.string,
				})
			),
		})
	).isRequired,

	appRoot: PropTypes.any.isRequired,
	children: PropTypes.node.isRequired,
	reactDOM: PropTypes.instanceOf(DocumentFragment),

	onInput: PropTypes.func,
	resetError: PropTypes.func,
	onChange: PropTypes.func.isRequired,
	setIsHasSelectedItem: PropTypes.func,
	setNextButtonDisabled: PropTypes.func,
	setShowPassengerInput: PropTypes.func,
	setSovMobileIsFromInputEmpty: PropTypes.func,
	handleSovMobileInputSelectionToView: PropTypes.func,
	handleFocus: PropTypes.func,
	labels: PropTypes.shape({
		noResults: PropTypes.string,
		list: PropTypes.string,
	}),
};

TypeaheadListViewInput.defaultProps = {
	helperText: "",
	mode: MODES.SEARCH,
	brand: BRANDS.SWG,
	className: undefined,
	placeholder: undefined,

	error: false,
	isLoading: false,
	isOnlyToField: false,
	isOnlyFromField: false,
	isHasSelectedItem: false,

	initialItem: undefined,
	selectedItem: undefined,

	reactDOM: document,
	onInput: () => {},
	resetError: () => {},
	setIsHasSelectedItem: () => {},
	setNextButtonDisabled: () => {},
	setShowPassengerInput: () => {},
	setSovMobileIsFromInputEmpty: () => {},
	handleFocus: () => {},
	handleSovMobileInputSelectionToView: () => {},

	labels: defaultLabels,
};

export default TypeaheadListViewInput;
export { TypeaheadListViewInput };
